import React, { useState, useEffect } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { securePut, secureGet } from '../../config/api';
import { useTranslation } from 'react-i18next';
import { Helmet } from "react-helmet-async";
import Input from '../../components/Input';
import { formatDateTime, formatAmount } from "../../utils/format";
import { SaveCancel } from '../../components/PrimaryButton';
import SnackbarNotification from '../../components/SnackbarNotification';
import LoadingLogo from '../../components/LoadingLogo';
import Breadcrumbs from '../../components/Breadcrumbs';
import plusIcon from '../../icons/plus.svg';
import DeleteIcon from '../../icons/trashR.svg';

const AvoirFinancierForm = ({ title, baseFields = [], listFields = [], initialValues = {}, onSuccess, onCancel }) => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const [formValues, setFormValues] = useState({});
  const [listItems, setListItems] = useState([]);
  const [totalTTC, setTotalTTC] = useState(0);
  const [loading, setLoading] = useState(!!id);
  const [specialProduct, setSpecialProduct] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [saving, setSaving] = useState(false);
  const [newItemValues, setNewItemValues] = useState({});

  useEffect(() => {
    const checkDataReadiness = () => {
      const baseFieldsReady = baseFields.every(field =>
        !field.options || field.options.length > 0
      );

      if (id) {
        return initialValues && Object.keys(initialValues).length > 0 && baseFieldsReady;
      }
      return baseFieldsReady;
    };

    if (checkDataReadiness()) {
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [initialValues, baseFields, id]);


  useEffect(() => {
    if (Object.keys(initialValues).length > 0) {
      setFormValues({
        id: initialValues.id || "",
        clientId: initialValues.client?.id || "",
        commercialId: initialValues.commercial?.id || "",
      });

      if (initialValues.details && Array.isArray(initialValues.details)) {
        setListItems(initialValues.details.map(item => ({
          designation: item.designation,
          priceTTC: Number(item.priceTTC),
        })));
        setTotalTTC(initialValues.priceTTC)
      }
    }
  }, [initialValues, baseFields]);

  useEffect(() => {
    const abortController = new AbortController();
    const fetchSpecialProduct = async () => {
      try {
        const response = await secureGet("/avoirsFinanciers/specialProduct", showSnackbar, { signal: abortController.signal });
        const specialProduct = response.data;
        setSpecialProduct(specialProduct);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error("Error fetching special product:", error);
          showSnackbar(t('errors.fetchSpecialProduct'), 'error');
        }
      }
    };
    fetchSpecialProduct();
    return () => {
      abortController.abort();
    };
  }, []);


  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleInputChange = (name, value) => {
    setFormValues(prev => ({ ...prev, [name]: value }));
  };

  const handleNewItemChange = (name, value) => {
    setNewItemValues(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // In modify mode, listFields are not required if listItems already exist
    const fieldsToValidate = listItems.length > 0 
        ? [...baseFields] 
        : [...baseFields, ...listFields];
        
    const missing = fieldsToValidate.filter(f => f.required && !formValues[f.name]);
    if (missing.length > 0) {
        showSnackbar(`Veuillez remplir les champs obligatoires : ${missing.map(f => f.label).join(', ')}`, 'error');
        return;
    }

    const payload = {
      ...formValues,
      details: listItems
        .filter(item => Object.values(item).some(val => val !== undefined && val !== ''))
        .map(item => ({
          ...item,
          priceTTC: Number(item.priceTTC),
          productId: specialProduct?.id,
        }))
    };

    setSaving(true);
    try {
      await securePut("/avoirsFinanciers/add", payload, showSnackbar);
      onSuccess();
      history.push({
        pathname: "/avoirs-financiers",
        state: {
          filters: location.state?.filters || {},
          page: location.state?.page || 0,
          size: location.state?.size || 30,
        },
      });
    } catch (error) {
      console.error('Submission error:', error);
    } finally {
      setSaving(false);
    }
  };

  const removeListItem = (index) => {
    const updatedItems = [...listItems];
    updatedItems.splice(index, 1);
    setListItems(updatedItems);
    setTotalTTC(updatedItems.reduce((acc, item) => acc + Number(item.priceTTC), 0));
  };

  const addListItem = () => {
    if (Object.values(newItemValues).some(val => val !== undefined && val !== '')) {
      const itemWithNegativePrice = {
        ...newItemValues,
        priceTTC: -Math.abs(Number(newItemValues.priceTTC))
      };

      setListItems(prev => [...prev, itemWithNegativePrice]);
      setNewItemValues({});
      setTotalTTC(prev => prev + itemWithNegativePrice.priceTTC);
    }
  };

  const handleCancel = () => {
    history.push({
      pathname: "/avoirs-financiers",
      state: {
        filters: location.state?.filters || {},
        page: location.state?.page || 0,
        size: location.state?.size || 30,
      },
    });
  };

  return (
    <div className="">
      <Helmet>
        <title>GrayFox - {t("titles.afinancier")}</title>
      </Helmet>
      <Breadcrumbs />

      <h2 className="text-[32px] text-[#38485C] font-semibold mb-6">{title}</h2>

      {loading ? (
        <div className="flex justify-center items-center h-[300px]">
          <LoadingLogo />
        </div>
      ) : (

        <form className="space-y-6">
          {/* Base Fields Section */}
            <div className="w-full flex flex-wrap items-end gap-4">
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {baseFields.map((field, index) => (
                  <Input
                    key={index}
                    type={field.type || 'text'}
                    label={field.label}
                    name={field.name}
                    value={formValues[field.name] || ''}
                    required={field.required}
                    onChange={(value) => handleInputChange(field.name, value)}
                    options={field.options || []}
                  />
                ))}
                {listFields.map((field, fieldIndex) => (
                  <Input
                    key={fieldIndex}
                    type={field.type || "text"}
                    label={field.label}
                    value={newItemValues[field.name] || ""}
                    onChange={(value) => handleNewItemChange(field.name, value)}
                    required={field.required}
                    options={field.options || []}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={addListItem}
                className="bg-[#043F88] text-white w-[48px] h-[48px] rounded-md hover:bg-[#3F3456] flex items-center justify-center"
              >
                <img src={plusIcon} alt="Add" className="w-4 h-4" />
              </button>
            </div>


          {/* List Section */}
          {listItems.length > 0 && (
            <div className="space-y-[10px]">
              <h3 className="text-[16px] font-semibold text-[#ACAFC6]">
                {t('fields_types.liste').toUpperCase()}
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {listItems.map((item, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center bg-white p-4 rounded-[12px] shadow-[0px_6px_26px_rgba(98,107,125,0.07)]"
                  >
                    <div className="flex flex-col">
                      <p className="text-[#0F172A] text-[16px] font-semibold">
                        {item.designation}
                      </p>
                      <p className="text-[#043F88] text-[14px] font-bold">
                        {formatAmount(item.priceTTC)}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => removeListItem(index)}
                      className="w-8 h-8 flex items-center justify-center bg-[#F9FAFC] rounded-[12px] border border-[#EF466F4D]"
                    >
                      <img src={DeleteIcon} alt="Delete" className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}



          {/* Totals and Footer */}
          <div className="flex justify-between items-center mt-6">
            <div className="flex text-left">
              <p className="text-[#231E29] text-[16px] font-medium">
                TOTAL TTC:
              </p>
              <p className="text-[#043F88] text-[16px] font-bold pl-2">
                {formatAmount(totalTTC)}
              </p>
            </div>
            <div className="space-x-4">
              <SaveCancel
                onSaveClick={handleSubmit}
                onCancelClick={handleCancel}
                saving={saving}
              />
            </div>
          </div>
        </form>
      )}

      {/* Snackbar Notification */}
      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      />
    </div>
  );
};

export default AvoirFinancierForm;
